import { useMemo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { z } from 'zod'
import type { MemoLogbookKind, MemoLogbookParams } from '@/entities/memo-book'

export const MAX_RANGE_DAYS = 366
export const RANGE_TOO_LONG_MESSAGE = 'Pick up to a year.'

const DAY_FORMAT = 'yyyy-MM-dd'
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const memoLogbookSearchSchema = {
  from: z.string().regex(DAY_PATTERN).optional().catch(undefined),
  to: z.string().regex(DAY_PATTERN).optional().catch(undefined),
  guard: z.coerce.number().int().positive().optional().catch(undefined),
  kind: z.enum(['all', 'notes']).optional().catch(undefined),
}

type MemoLogbookSearch = {
  from?: string
  to?: string
  guard?: number
  kind?: MemoLogbookKind
}

export interface MemoLogbookFilterState {
  range: DateRange | undefined
  setRange: (range: DateRange | undefined) => void
  guardUserId: number | undefined
  setGuardUserId: (id: number | undefined) => void
  kind: MemoLogbookKind
  setKind: (kind: MemoLogbookKind) => void
  rangeError: string | null
  params: MemoLogbookParams
  rangeIsValid: boolean
}

const toDay = (value: string | undefined): Date | undefined => {
  if (!value) return undefined
  const date = parseISO(value)
  return isValid(date) ? date : undefined
}

export function useMemoLogbookFilters(): MemoLogbookFilterState {
  const search = useSearch({ strict: false }) as MemoLogbookSearch
  const navigate = useNavigate()

  const update = (patch: MemoLogbookSearch) =>
    void navigate({
      to: '.',
      search: (previous: Record<string, unknown>) => ({ ...previous, ...patch }),
      replace: true,
    } as never)

  const range = useMemo<DateRange | undefined>(() => {
    const from = toDay(search.from)
    return from ? { from, to: toDay(search.to) } : undefined
  }, [search.from, search.to])

  const guardUserId = search.guard
  const kind: MemoLogbookKind = search.kind ?? 'all'

  const rangeError =
    range?.from && range.to && differenceInCalendarDays(range.to, range.from) + 1 > MAX_RANGE_DAYS
      ? RANGE_TOO_LONG_MESSAGE
      : null

  const params = useMemo<MemoLogbookParams>(() => {
    const from = range?.from ? format(range.from, DAY_FORMAT) : undefined
    const to = range?.from ? format(range.to ?? range.from, DAY_FORMAT) : undefined
    return { from, to, guardUserId, kind }
  }, [range, guardUserId, kind])

  return {
    range,
    setRange: (next) =>
      update({
        from: next?.from ? format(next.from, DAY_FORMAT) : undefined,
        to: next?.to ? format(next.to, DAY_FORMAT) : undefined,
      }),
    guardUserId,
    setGuardUserId: (id) => update({ guard: id }),
    kind,
    setKind: (next) => update({ kind: next === 'all' ? undefined : next }),
    rangeError,
    params,
    rangeIsValid: rangeError === null,
  }
}
